import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode, Ref } from 'react';
import clsx from 'clsx';
import './NavRow.css';

export interface NavRowProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'title'> {
  /** Leading visual — typically a tinted icon tile. */
  icon?: ReactNode;
  title: ReactNode;
  /** Second line under the title (status, date, hint). */
  sub?: ReactNode;
  /** Right-aligned slot before the chevron (count, chip, value). */
  trailing?: ReactNode;
  /** Hide the chevron for rows that act in place rather than navigate. */
  chevron?: boolean;
  onPress: () => void;
  /**
   * 'card'  — standalone surface card with border + extruded edge.
   * 'row'   — flat row for stacking inside a RowGroup (divider-separated).
   */
  variant?: 'card' | 'row';
  ref?: Ref<HTMLButtonElement>;
}

/**
 * NavRow — a tappable "door": icon tile, title/sub, trailing slot, chevron.
 * The list-row workhorse for dashboard screens (Profile's subscription
 * summary, settings links, awards shortcut). Card variant carries the
 * bold-pass border + hard edge and Button press physics; row variant is
 * flat for grouping several rows in one card.
 */
export function NavRow({
  icon,
  title,
  sub,
  trailing,
  chevron = true,
  onPress,
  variant = 'card',
  className,
  ref,
  ...rest
}: NavRowProps) {
  return (
    <button
      type="button"
      className={clsx('fluent-nav-row', className)}
      data-variant={variant}
      onClick={onPress}
      ref={ref}
      {...rest}
    >
      {icon && (
        <span className="fluent-nav-row__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="fluent-nav-row__text">
        <span className="fluent-nav-row__title">{title}</span>
        {sub != null && <span className="fluent-nav-row__sub">{sub}</span>}
      </span>
      {trailing != null && <span className="fluent-nav-row__trailing">{trailing}</span>}
      {chevron && (
        <svg
          className="fluent-nav-row__chevron"
          aria-hidden="true"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m9 6 6 6-6 6" />
        </svg>
      )}
    </button>
  );
}

export interface RowGroupProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

/** RowGroup — one bold-pass card that stacks `variant="row"` NavRows with
 *  hairline dividers, iOS-Settings style. */
export function RowGroup({ children, className, ref, ...rest }: RowGroupProps) {
  return (
    <div className={clsx('fluent-row-group', className)} ref={ref} {...rest}>
      {children}
    </div>
  );
}
